
import { logger } from '@/utils/logger';

/**
 * Check if a response string appears to be HTML instead of JSON
 */
export function isHtmlResponse(responseText: string): boolean {
  const htmlIndicators = ['<!doctype', '<html', '<body', '<head', '<div', '<script'];
  const lowerText = responseText.toLowerCase().trim();
  
  // Log that we're detecting a potentially problematic response
  if (htmlIndicators.some(indicator => lowerText.includes(indicator))) {
    const htmlError = new Error('HTML response detected instead of expected JSON');
    logger.error('HTML response detected instead of expected JSON', htmlError, {
      component: 'apiValidation'
    });
    return true;
  }
  
  return false;
}

/**
 * Extract meaningful error information from HTML responses
 */
export function extractErrorFromHtml(html: string): { message: string; code: string } {
  const defaultResponse = { message: 'Unknown error in HTML response', code: 'unknown' };
  
  if (!html || typeof html !== 'string') {
    const invalidHtmlError = new Error('Invalid HTML provided for error extraction');
    logger.error('Invalid HTML provided for error extraction', invalidHtmlError, {
      component: 'apiValidation'
    });
    return defaultResponse;
  }
  
  // Look for HTTP status code in title
  const titleMatch = html.match(/<title>(\d+)/i);
  const statusCode = titleMatch ? titleMatch[1] : 'unknown';
  
  // Check for common error patterns
  if (html.includes('rate limit') || html.includes('too many requests')) {
    return { message: 'Rate limit exceeded', code: statusCode || '429' };
  }
  
  if (html.includes('invalid') && html.includes('key')) {
    return { message: 'Invalid API key', code: statusCode || '401' };
  }
  
  if (html.includes('authentication') || html.includes('unauthorized')) {
    return { message: 'Authentication failed', code: statusCode || '401' };
  }
  
  if (html.includes('billing') || html.includes('payment')) {
    return { message: 'Billing issue', code: statusCode || '402' };
  }
  
  // If we couldn't identify a specific error
  if (statusCode !== 'unknown') {
    return { message: `HTTP Error ${statusCode}`, code: statusCode };
  }
  
  return defaultResponse;
}
